import { Card, CardContent } from "@bowfast/ui/components/card";
import { Skeleton } from "@bowfast/ui/components/skeleton";
import { cn } from "@bowfast/ui/lib/utils";
import { motion } from "framer-motion";
import { fadeUp } from "~/lib/animations";
import type { RouteKPI } from "~/pages/routes/routes.types";

interface RouteKPIStripProps {
  readonly kpis: RouteKPI[];
  readonly isPending: boolean;
}

export function RouteKPIStrip({ kpis, isPending }: RouteKPIStripProps) {
  if (isPending) {
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {([0, 1, 2, 3] as const).map((i) => (
          <Skeleton key={i} className="h-22 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {kpis.map((kpi, i) => {
        const Icon = kpi.Icon;
        return (
          <motion.div key={kpi.label} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 + i * 0.05 }}>
            <Card className="group relative overflow-hidden transition-shadow hover:shadow-md">
              <div className={cn("absolute inset-x-0 top-0 h-[3px] rounded-t-xl", kpi.accentClass)} />
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[0.625rem] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                      {kpi.label}
                    </p>
                    <p className="mt-1 font-heading text-xl font-bold tabular-nums leading-none tracking-[-0.02em]">
                      {kpi.value}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">{kpi.sub}</p>
                  </div>
                  <div
                    className={cn(
                      "flex size-9 shrink-0 items-center justify-center rounded-xl transition-transform group-hover:scale-110",
                      kpi.iconBg,
                    )}
                  >
                    <Icon className={cn("size-4", kpi.iconColor)} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );
}
